import React from 'react';
import _ from 'underscore';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {Registry} from 'constants/RegistryConstants';
import {
  ContainableSummary,
  CreateStorablePanes,
  InitialParentStorable,
  MODAL_PANE_TO_TITLE,
  ModalPane,
  StorableModalCreateProps,
  ValidationErrors,
} from 'constants/SampleConstants';
import * as Context from 'Context';
import NavStore from 'nav/NavStore';
import {getParentStorableDatas} from 'samples/storables/ParentStorableUtil';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

import CreateContainerPane, {
  CreateContainersPaneState,
  DEFAULT_CREATE_CONTAINERS_PANE_STATE,
} from './containers/CreateContainerPane';
import CreateBoxPane, {CreateBoxPaneState, DEFAULT_CREATE_BOX_PANE_STATE} from './CreateBoxPane';
import CreateLocationPane, {
  CreateLocationPaneState,
  DEFAULT_CREATE_LOCATION_PANE_STATE,
} from './CreateLocationPane';
import CreatePlatePane, {CreatePlatePaneState, DEFAULT_CREATE_PLATE_PANE_STATE} from './CreatePlatePane';
import * as SampleUtil from './SampleUtil';
import * as StorableModalsUtil from './StorableModalsUtil';

class CreateStorableModalConnector extends DataConnector<
  CreateStorableModalProps,
  CreateStorableModalEnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [NavStore];

  enhanceProps(): CreateStorableModalEnhancedProps {
    return {
      numRegistries: this.navStore().getRegistries().length,
    };
  }
}

export type CreateStorableModalPaneState =
  | CreateContainersPaneState
  | CreateBoxPaneState
  | CreatePlatePaneState
  | CreateLocationPaneState;

export interface PaneProps {
  disallowContainableEdit: boolean;
  shouldShowPlaceholderContainable: boolean;
}

export interface CreateStorableModalProps {
  initialInputContainable?: ContainableSummary;
  initialParentStorable: InitialParentStorable | null;
  initialRegistry: Registry | null;
  modalPane: CreateStorablePanes;
  onCreate: (
    onFinish: (errorMessage?: string) => void,
    createPropsArray?: Array<StorableModalCreateProps>,
    paneState?: Partial<CreateStorableModalPaneState>
  ) => void;
  onRequestClose: () => void;
  paneClass:
    | typeof CreateContainerPane
    | typeof CreatePlatePane
    | typeof CreateBoxPane
    | typeof CreateLocationPane;
  paneProps?: PaneProps;
  isParentStorableRequired?: boolean;
  analyticsMetadata?: AnalyticsMetadataForContext;
}
interface CreateStorableModalEnhancedProps {
  numRegistries: number;
}

const DEFAULT_CREATE_PANE_STATE: {
  [modalPane in CreateStorablePanes]: CreateStorableModalPaneState;
} = {
  [ModalPane.CREATE_BOX]: DEFAULT_CREATE_BOX_PANE_STATE,
  [ModalPane.CREATE_CONTAINERS]: DEFAULT_CREATE_CONTAINERS_PANE_STATE,
  [ModalPane.CREATE_LOCATION]: DEFAULT_CREATE_LOCATION_PANE_STATE,
  [ModalPane.CREATE_PLATE]: DEFAULT_CREATE_PLATE_PANE_STATE,
};

function getValidationErrors(
  paneKey: CreateStorablePanes,
  paneState: CreateStorableModalPaneState
): ValidationErrors {
  switch (paneKey) {
    case ModalPane.CREATE_CONTAINERS:
      return StorableModalsUtil.getCreateContainerValidationErrors(paneState as CreateContainersPaneState);
    case ModalPane.CREATE_BOX:
      return StorableModalsUtil.getCreateBoxValidationErrors(paneState as CreateBoxPaneState);
    case ModalPane.CREATE_PLATE:
      return StorableModalsUtil.getCreatePlateValidationErrors(paneState as CreatePlatePaneState);
    case ModalPane.CREATE_LOCATION:
      return StorableModalsUtil.getCreateLocationValidationErrors(paneState as CreateLocationPaneState);
    default:
      assert(false, `Unexpected create storable pane: ${paneKey}`);
  }
}

class CreateStorableModal extends React.Component<
  CreateStorableModalProps & CreateStorableModalEnhancedProps
> {
  static defaultProps: Partial<CreateStorableModalProps> = {
    paneProps: {
      disallowContainableEdit: false,
      shouldShowPlaceholderContainable: false,
    },
    isParentStorableRequired: false,
  };
  override state: CreateStorableModalPaneState = {
    ...DEFAULT_CREATE_PANE_STATE[this.props.modalPane],
    registry: this.props.initialRegistry,
    selectedParent: {storable: this.props.initialParentStorable, gridCells: []},
    inputContainable: this.props.initialInputContainable ?? null,
  };

  override componentDidUpdate(
    prevProps: CreateStorableModalProps,
    prevState: CreateStorableModalPaneState
  ): void {
    if (prevState.isCreating && !this.state.isCreating && _.isEmpty(this.state.validationErrors)) {
      prevProps.onRequestClose();
    }
  }

  private handleUpdate = (state: Partial<CreateStorableModalPaneState>): void => {
    this.setState(state);
  };

  private handleCompleteSubmitAttempt = (errorMessage?: string): void => {
    const spreadsheetState =
      this.props.modalPane === ModalPane.CREATE_PLATE && errorMessage == null
        ? {spreadsheetFile: null, spreadsheetUrl: null}
        : {};
    this.setState({
      isCreating: false,
      validationErrors: errorMessage != null ? {formError: errorMessage} : {},
      ...spreadsheetState,
    });
  };

  private handleCreate = (): void => {
    let numToCreate = this.state.numToCreate;
    const selectedParent = this.state.selectedParent;

    if (
      selectedParent.storable &&
      SampleUtil.isGridId(selectedParent.storable.id) &&
      selectedParent.gridCells.length
    ) {
      numToCreate = selectedParent.gridCells.length;
    }

    const errors = getValidationErrors(this.props.modalPane, this.state);
    this.handleUpdate({validationErrors: errors});
    if (_.isEmpty(errors)) {
      // Create array of barcodes with a barcode for each new storable.
      let barcodes;
      if (this.state.shouldAutogenerateBarcode) {
        barcodes = Array(numToCreate).fill(null);
      } else if (numToCreate === 1) {
        barcodes = [this.state.customBarcode.trim()];
      } else {
        const barcodeInputs = this.state.customBarcode.split(/[\s,]+/);
        assert(barcodeInputs.length === numToCreate, `Expected ${numToCreate} barcodes.`);
        barcodes = barcodeInputs.map((barcode: string) => barcode.trim());
      }

      // Create array of parentStorableData.
      const parentStorableDatas = getParentStorableDatas(numToCreate, this.state.selectedParent);

      const createPropsArray: Array<StorableModalCreateProps> = _.zip(barcodes, parentStorableDatas).map(
        ([barcode, parentStorableData]) => ({
          barcode,
          parentStorableData,
          name: this.state.name,
          selectedSchemaId: this.state.selectedSchema!.id,
          sourceId: this.state.sourceId!,
          tags: this.state.tags,
        })
      );

      this.handleUpdate({isCreating: true});
      Context.usingAsync({analyticsMetadata: this.props.analyticsMetadata ?? {}}, null, () => {
        this.props.onCreate(this.handleCompleteSubmitAttempt, createPropsArray, this.state);
      });
    }
  };

  private getButtonText = (): string => {
    const modalPaneState = this.state as CreatePlatePaneState;
    if (StorableModalsUtil.isSpreadsheetLoading(modalPaneState)) {
      return 'Loading...';
    }
    if (modalPaneState.isCreating) {
      return 'Creating...';
    }
    return 'Create';
  };

  private isCreateDisabled = (): boolean => {
    const modalPaneState = this.state as CreatePlatePaneState;
    return (
      modalPaneState.isCreating ||
      modalPaneState.isValidating ||
      StorableModalsUtil.isSpreadsheetLoading(modalPaneState)
    );
  };

  override render(): JSX.Element {
    const ModalPaneClass = this.props.paneClass as React.ElementType;
    return (
      <Modal
        buttons={[
          {
            disabled: this.isCreateDisabled(),
            text: this.getButtonText(),
            style: ButtonStyle.PRIMARY,
            submit: true,
            dataTestAttrs: {
              component: 'CreateStorableModal',
              element: 'submitButton',
            },
          },
        ]}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleCreate}
        title={MODAL_PANE_TO_TITLE[this.props.modalPane]}
        scrollable={true}
        expandHeight={true}
      >
        <ModalPaneClass
          {...this.state}
          {...this.props.paneProps}
          numRegistries={this.props.numRegistries}
          onUpdate={this.handleUpdate}
          registryId={this.state.registry?.id}
        />
      </Modal>
    );
  }
}

export default CreateStorableModalConnector.connect(CreateStorableModal);
